import { FC } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Icon, IconColor, IconTypes } from '@app/ui'

type SupportRequestType = 'support' | 'bug' | 'feature'

interface HelpCenterScreenProps {
  title: string
  onBack: () => void
  onContactSupport: (type: SupportRequestType) => void
  onLegalClick: () => void
}

const HelpCenterScreen: FC<HelpCenterScreenProps> = ({ title, onBack, onContactSupport, onLegalClick }) => {
  const { t } = useTranslation()

  return (
    <StyledScreen>
      <StyledBackButton onClick={onBack}>
        <Icon type={IconTypes.angleLeft} color={IconColor.textPrimary} width={18} height={18} />
        <span>{title}</span>
      </StyledBackButton>

      <StyledHeader>
        <StyledSupportIcon>
          <img src="/assets/images/question-mark.svg?v=2" alt="" />
        </StyledSupportIcon>
        <h2>{t('helpCenter.title')}</h2>
        <p>{t('helpCenter.subtitle')}</p>
      </StyledHeader>

      <StyledSection>
        <StyledSectionLabel>{t('helpCenter.section.support')}</StyledSectionLabel>
        <StyledListCard>
          <StyledActionRow onClick={() => onContactSupport('support')}>
            <StyledIconBox>
              <img src="/assets/images/help-center/headphones.svg" alt="" />
            </StyledIconBox>
            <StyledActionText>
              <span>{t('helpCenter.contactSupport.title')}</span>
              <small>{t('helpCenter.contactSupport.subtitle')}</small>
            </StyledActionText>
            <Icon type={IconTypes.angleRight} color={IconColor.zinc400} width={16} height={16} />
          </StyledActionRow>

          <StyledActionRow onClick={() => onContactSupport('bug')}>
            <StyledIconBox>
              <img src="/assets/images/help-center/bugreport.svg" alt="" />
            </StyledIconBox>
            <StyledActionText>
              <span>{t('helpCenter.reportBug.title')}</span>
              <small>{t('helpCenter.reportBug.subtitle')}</small>
            </StyledActionText>
            <Icon type={IconTypes.angleRight} color={IconColor.zinc400} width={16} height={16} />
          </StyledActionRow>

          <StyledActionRow onClick={() => onContactSupport('feature')}>
            <StyledIconBox>
              <img src="/assets/images/help-center/feature.svg" alt="" />
            </StyledIconBox>
            <StyledActionText>
              <span>{t('helpCenter.requestFeature.title')}</span>
              <small>{t('helpCenter.requestFeature.subtitle')}</small>
            </StyledActionText>
            <Icon type={IconTypes.angleRight} color={IconColor.zinc400} width={16} height={16} />
          </StyledActionRow>
        </StyledListCard>
      </StyledSection>

      <StyledFooter>
        <span>{t('helpCenter.footer.text')}</span>
        <button onClick={onLegalClick}>{t('helpCenter.footer.legal')}</button>
      </StyledFooter>
    </StyledScreen>
  )
}

const StyledScreen = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 44px 20px 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background:
    linear-gradient(
      180deg,
      #24113c 0%,
      #1b1030 9%,
      #0d0d14 20%,
      #08080c 100%
    );
  position: relative;
  overflow: hidden auto;

  [data-theme="light"] & {
    background:
      linear-gradient(
        180deg,
        #EDE9FE 0%,
        #F3F0FF 9%,
        #FAFAFA 20%,
        var(--bg-primary) 100%
      );
  }

  &::before {
    content: '';
    position: absolute;
    top: -185px;
    left: 50%;
    transform: translateX(-50%);
    width: 420px;
    height: 420px;
    border-radius: 50%;
    pointer-events: none;
    background:
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.45) 0%,
        rgba(110, 0, 255, 0.25) 35%,
        rgba(80, 0, 180, 0.08) 60%,
        transparent 80%
      );
    filter: blur(40px);
  }

  [data-theme="light"] &::before {
    background:
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.18) 0%,
        rgba(110, 0, 255, 0.10) 35%,
        rgba(80, 0, 180, 0.04) 60%,
        transparent 80%
      );
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const StyledBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  padding: 10px 0;
  margin-bottom: 48px;
  cursor: pointer;
`

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 26px;

  h2 {
    color: var(--text-primary);
    font-size: 20px;
    font-weight: 600;
    margin: 16px 0 8px;
  }

  p {
    color: var(--text-quaternary);
    font-size: 13px;
    margin: 0;
  }
`

const StyledSupportIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;

  img {
    width: 88px;
    height: 88px;
  }
`

const StyledSection = styled.section`
  margin-top: 16px;
`

const StyledSectionLabel = styled.h3`
  color: var(--text-quaternary);
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 8px;
`

const StyledListCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledActionRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  padding: 14px 12px;
  cursor: pointer;
  text-align: left;

  [data-theme="light"] & {
    border: 1px solid var(--border-primary);
    background: var(--bg-quaternary);
  }
`

const StyledIconBox = styled.div<{ $iconFullSize?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);

  [data-theme="light"] & {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
  }

  img {
    width: ${({ $iconFullSize }) => ($iconFullSize ? '34px' : '18px')};
    height: ${({ $iconFullSize }) => ($iconFullSize ? '34px' : '18px')};
  }
`

const StyledActionText = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;

  span {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
  }

  small {
    color: var(--text-quaternary);
    font-size: 11px;
    line-height: 16px;
  }
`

const StyledFooter = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
  padding-top: 28px;
  text-align: center;

  span {
    color: var(--text-primary);
    font-size: 11px;
  }

  button {
    border: none;
    background: transparent;
    color: var(--violet-400);
    font-size: 11px;
    padding: 4px 0;
    cursor: pointer;
  }
`

export default HelpCenterScreen
